/**
 * Number Inventory Store
 *
 * Drives the Super Admin "Number Inventory" page and the per-account
 * "Assign Existing Number" dialog. Talks to the new
 * /api/super-admin/inventory/* endpoints.
 *
 * Each Telnyx-account number is shown alongside its local PhoneNumber row (if
 * any) plus the list of tenants currently assigned to it. The Super Admin can
 * then import + assign in one click; behind the scenes the backend ensures
 * the local row exists and writes PhoneNumberAssignment rows.
 */
import { create } from 'zustand';
import { getApiClient, API_ENDPOINTS } from '@respectly/api-client';

export type InventoryStatusFilter = 'all' | 'unassigned' | 'dedicated' | 'shared';
export type InventoryCapabilityFilter = 'sms' | 'mms' | 'voice' | undefined;
export type InventoryTypeFilter = 'local' | 'tollfree' | 'mobile' | undefined;

export interface InventoryAssignment {
  tenantId: string;
  tenantName: string;
  isDefault: boolean;
  assignedAt: string | Date;
}

export interface InventoryItem {
  telnyx: {
    id: string;
    phoneNumber: string;
    type: string;
    capabilities: { sms: boolean; mms: boolean; voice: boolean };
    messagingProfileId: string | null;
    status: string | null;
  };
  local:
    | {
        id: string;
        isActive: boolean;
        isShared: boolean;
        importedFromTelnyx: boolean;
        friendlyName: string | null;
        monthlyFee: number;
      }
    | null;
  assignments: InventoryAssignment[];
  inDb: boolean;
}

interface InventoryFilters {
  status: InventoryStatusFilter;
  capability: InventoryCapabilityFilter;
  type: InventoryTypeFilter;
  excludeAssignedTenantId?: string;
}

interface InventoryError {
  code: string;
  message: string;
}

interface InventoryState {
  items: InventoryItem[];
  isLoading: boolean;
  loadError: InventoryError | null;
  filters: InventoryFilters;

  isAssigning: boolean;
  isUnassigning: boolean;
  actionError: InventoryError | null;

  setFilter: <K extends keyof InventoryFilters>(key: K, value: InventoryFilters[K]) => void;
  load: (overrides?: Partial<InventoryFilters>) => Promise<void>;
  /**
   * Imports a Telnyx number into the local DB if missing, then assigns it to the
   * given tenants. Returns true if the operation succeeded.
   */
  assignToTenants: (params: {
    telnyx: InventoryItem['telnyx'];
    monthlyFee?: number;
    tenantIds: string[];
    setDefaultForTenantId?: string | null;
  }) => Promise<boolean>;
  unassign: (phoneNumberId: string, tenantId: string) => Promise<boolean>;
  setDefault: (phoneNumberId: string, tenantId: string, isDefault: boolean) => Promise<boolean>;
  clearError: () => void;
}

function toError(err: unknown): InventoryError {
  if (err && typeof err === 'object') {
    const e = err as {
      code?: string;
      message?: string;
      error?: { code?: string; message?: string };
    };
    if (e.error) {
      return { code: e.error.code ?? 'UNKNOWN', message: e.error.message ?? 'Unknown error' };
    }
    return { code: e.code ?? 'UNKNOWN', message: e.message ?? 'Unknown error' };
  }
  return { code: 'UNKNOWN', message: typeof err === 'string' ? err : 'Unknown error' };
}

function buildQuery(filters: InventoryFilters): string {
  const params = new URLSearchParams();
  if (filters.status && filters.status !== 'all') params.set('status', filters.status);
  if (filters.capability) params.set('capability', filters.capability);
  if (filters.type) params.set('type', filters.type);
  if (filters.excludeAssignedTenantId)
    params.set('excludeAssignedTenantId', filters.excludeAssignedTenantId);
  const q = params.toString();
  return q ? `?${q}` : '';
}

export const useNumberInventoryStore = create<InventoryState>((set, get) => ({
  items: [],
  isLoading: false,
  loadError: null,
  filters: { status: 'all', capability: undefined, type: undefined },

  isAssigning: false,
  isUnassigning: false,
  actionError: null,

  setFilter(key, value) {
    set((s) => ({ filters: { ...s.filters, [key]: value } }));
  },

  async load(overrides) {
    const filters = overrides
      ? { ...get().filters, ...overrides }
      : get().filters;
    set({ isLoading: true, loadError: null });
    try {
      const api = getApiClient();
      const items = await api.get<InventoryItem[]>(
        `${API_ENDPOINTS.SUPER_ADMIN.ACCOUNTS.INVENTORY.LIST_TELNYX}${buildQuery(filters)}`,
      );
      set({ items, isLoading: false });
    } catch (err) {
      set({ isLoading: false, loadError: toError(err) });
    }
  },

  async assignToTenants({ telnyx, monthlyFee, tenantIds, setDefaultForTenantId }) {
    set({ isAssigning: true, actionError: null });
    try {
      const api = getApiClient();

      // Find the local row for this telnyx number (if already imported).
      const existingLocal = get().items.find((i) => i.telnyx.id === telnyx.id)?.local ?? null;

      let phoneNumberId = existingLocal?.id;
      if (!phoneNumberId) {
        const imported = await api.post<{ id: string }>(
          API_ENDPOINTS.SUPER_ADMIN.ACCOUNTS.INVENTORY.IMPORT,
          {
            telnyxNumberId: telnyx.id,
            e164: telnyx.phoneNumber,
            type: telnyx.type,
            capabilities: telnyx.capabilities,
            monthlyFee: monthlyFee ?? 0,
          },
        );
        phoneNumberId = imported.id;
      }

      await api.post(
        API_ENDPOINTS.SUPER_ADMIN.ACCOUNTS.INVENTORY.ASSIGNMENTS(phoneNumberId),
        {
          tenantIds,
          ...(setDefaultForTenantId ? { setDefaultForTenantId } : {}),
        },
      );

      await get().load();
      set({ isAssigning: false });
      return true;
    } catch (err) {
      set({ isAssigning: false, actionError: toError(err) });
      return false;
    }
  },

  async unassign(phoneNumberId, tenantId) {
    set({ isUnassigning: true, actionError: null });
    try {
      const api = getApiClient();
      await api.delete(
        API_ENDPOINTS.SUPER_ADMIN.ACCOUNTS.INVENTORY.ASSIGNMENT(phoneNumberId, tenantId),
      );
      await get().load();
      set({ isUnassigning: false });
      return true;
    } catch (err) {
      set({ isUnassigning: false, actionError: toError(err) });
      return false;
    }
  },

  async setDefault(phoneNumberId, tenantId, isDefault) {
    set({ actionError: null });
    try {
      const api = getApiClient();
      await api.patch(
        API_ENDPOINTS.SUPER_ADMIN.ACCOUNTS.INVENTORY.ASSIGNMENT(phoneNumberId, tenantId),
        { isDefault },
      );
      await get().load();
      return true;
    } catch (err) {
      set({ actionError: toError(err) });
      return false;
    }
  },

  clearError() {
    set({ loadError: null, actionError: null });
  },
}));
